import { useState, useEffect, useCallback } from 'react'
import { departmentsApi, type Department } from '../api/team'
import { businessLinesApi, type BusinessLineWithDepartments } from '../api/businessLines'
import { useFeatureFlag } from '../lib/featureFlags'
import { SignatureSectorRulesCard } from '../components/SignatureSectorRulesCard'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Checkbox,
  useSnackbar,
  useConfirm,
  FormGrid,
  Select,
} from '../components/ui'

interface DepartmentFormState {
  name: string
  sortOrder: number
  hasOperators: boolean
}

const EMPTY_FORM: DepartmentFormState = {
  name: '',
  sortOrder: 0,
  hasOperators: false,
}

/** `canAssignBranch`: il ramo del reparto lo cambia solo l'amministrazione (l'API è solo Admin). */
export function DepartmentsPage({ canAssignBranch = false }: { canAssignBranch?: boolean }) {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<Department[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<DepartmentFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // ADR-019 (Equal Point): con il flag acceso ogni reparto sceglie il suo ramo (anche quelli ancora spenti).
  const equalPoint = useFeatureFlag('equalpoint.rami')
  const [lines, setLines] = useState<BusinessLineWithDepartments[]>([])
  useEffect(() => {
    if (!equalPoint) return
    let alive = true
    businessLinesApi.list(true).then(r => { if (alive) setLines(r) }).catch(() => { if (alive) setLines([]) })
    return () => { alive = false }
  }, [equalPoint])

  const [savingBranch, setSavingBranch] = useState<number | null>(null)
  async function changeBranch(d: Department, value: string) {
    const businessLineId = value === '' ? null : Number(value)
    setSavingBranch(d.id)
    try {
      await businessLinesApi.assignDepartment(d.id, businessLineId)
      setItems(prev => prev.map(x => (x.id === d.id ? { ...x, businessLineId } : x)))
      snackbar.show({ message: `Ramo di «${d.name}» aggiornato` })
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Ramo non aggiornato', tone: 'error' })
    } finally {
      setSavingBranch(null)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await departmentsApi.list()
      setItems(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(d: Department) {
    setForm({
      name: d.name,
      sortOrder: d.sortOrder,
      hasOperators: d.hasOperators ?? false,
    })
    setEditId(d.id)
    setSaveError('')
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError('Il nome è obbligatorio')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editId !== null) {
        await departmentsApi.update(editId, {
          name: form.name,
          sortOrder: form.sortOrder,
          hasOperators: form.hasOperators,
        })
      } else {
        await departmentsApi.create(form.name, form.sortOrder, form.hasOperators)
      }
      closeModal()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(d: Department) {
    if (!(await askConfirm({ title: 'Elimina reparto', message: `Eliminare il reparto "${d.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await departmentsApi.delete(d.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante l'eliminazione", tone: 'error' })
    }
  }

  return (
    <Page>
      <PageHeader
        title="Reparti"
        icon="corporate_fare"
        count={items.length > 0 ? items.length : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuovo reparto
          </Button>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="corporate_fare" title="Nessun reparto" />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th>Operatori</th>
                {equalPoint && <th>Ramo</th>}
                <th>Ordine</th>
                <th>Stato</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr key={d.id}>
                  <td className="text-muted md-num">{d.id}</td>
                  <td className="md-typescale-emphasized-body-medium">{d.name}</td>
                  <td>
                    {d.hasOperators ? (
                      <StatusPill tone="success" icon="check">Sì</StatusPill>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  {equalPoint && (
                    <td>
                      <Select id={`dep-line-${d.id}`} label="Ramo" labelHidden aria-label={`Ramo di ${d.name}`}
                        value={d.businessLineId?.toString() ?? ''} disabled={!canAssignBranch || savingBranch === d.id}
                        onChange={e => void changeBranch(d, e.target.value)}>
                        <option value="">—</option>
                        {lines.map(l => <option key={l.id} value={l.id}>{l.name}{l.isActive ? '' : ' (spento)'}</option>)}
                      </Select>
                    </td>
                  )}
                  <td className="text-muted md-num">{d.sortOrder}</td>
                  <td>
                    {d.isActive ? (
                      <StatusPill tone="success">Attivo</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Inattivo</StatusPill>
                    )}
                  </td>
                  <td className="text-end">
                    <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(d)} />{' '}
                    <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void handleDelete(d)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <SignatureSectorRulesCard />

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica reparto' : 'Nuovo reparto'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          <FormGrid columns={1}>
            <TextField
              label="Nome *"
              placeholder="Nome reparto"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Ordine"
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
            />
            <Checkbox
              id="hasOperators"
              label="Ha operatori assegnati"
              checked={form.hasOperators}
              onChange={e => setForm(f => ({ ...f, hasOperators: e.target.checked }))}
            />
          </FormGrid>
        </>
      </Dialog>
    </Page>
  )
}
